import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';

import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';

import { createApp } from '@backstage/frontend-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { TechInsightsScorecardPage } from '@backstage-community/plugin-tech-insights';
import { CostInsightsPage } from '@backstage-community/plugin-cost-insights';
import { DevToolsPage } from '@backstage/plugin-devtools';

import feedbackPlugin from '@internal/plugin-feedback/alpha';
import secretsConfigsPlugin from '@internal/plugin-secrets-configs/alpha';
import lassoPlugin from '@internal/plugin-lasso-react/alpha';
import armoryPlugin from '@internal/plugin-armory/alpha';

import '@backstage/ui/css/styles.css';

import {
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { orgPlugin } from '@backstage/plugin-org';
import { HomePage } from './components/home/HomePage';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { KaminoPage } from '@internal/plugin-kamino';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { appModule } from './modules/app';

/**
 * Routes that have not yet been migrated to the new frontend system. As
 * plugins gain new-frontend-system support, move them out of here and into
 * the `features` list below instead.
 */
const legacyRoutes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="/home" />} />
    <Route path="/home" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/tech-insights" element={<TechInsightsScorecardPage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/kamino/*" element={<KaminoPage />} />
    <Route
      path="/entity-validation"
      element={<Navigate to="/devtools/entity-validation" replace />}
    />
    <Route
      path="/catalog-unprocessed-entities"
      element={<Navigate to="/devtools/unprocessed-entities" replace />}
    />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
  </FlatRoutes>
);

const app = createApp({
  features: [
    appModule,
    feedbackPlugin,
    secretsConfigsPlugin,
    lassoPlugin,
    armoryPlugin,
    ...convertLegacyAppRoot(legacyRoutes),
  ],
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(
        scaffolderPlugin.routes.selectedTemplate,
      ),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(
        catalogImportPlugin.routes.importPage,
      ),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
    bind(feedbackPlugin.externalRoutes, {
      viewDocs: convertLegacyRouteRef(techdocsPlugin.routes.root),
    });
  },
});

export default app.createRoot();
